/**
 * Column Order Management Utilities
 *
 * Utilities for extracting plan order from AgGrid column movement events.
 */

import { ColumnMovedEvent } from "ag-grid-community";
import { logger } from "@platform/browser-logger";

/**
 * Extracts column order from a column moved event using column IDs
 * This works with the current implementation that uses plan identifiers as column IDs
 *
 * @param event - AgGrid ColumnMovedEvent
 * @returns Array of plan identifiers in new order, or null if not applicable
 */
export const extractPlanOrderFromEvent = (
  event: ColumnMovedEvent,
): string[] | null => {
  try {
    // Only process finished move events
    if (!event.finished) return null;

    const columnState = event.api.getColumnState();

    // Filter to get only top-level plan columns (exclude benefits and internal columns)
    // Plan columns have fields like "BL-1-1", "DEN-1-1.inNetwork" or "DEN-1-1"
    const planColumns = columnState
      .filter(
        (col) =>
          col.colId &&
          col.colId !== "benefit.label" && // Exclude benefits column
          !col.colId.startsWith("ag-Grid") && // Exclude AG Grid internal columns
          /^[A-Z]+(-\d+)*-\d+/.test(col.colId), // Match plan identifier pattern (more flexible)
      )
      .map((col) => {
        // Extract base identifier (e.g., "BL-1-1" from "BL-1-1" or "DEN-1-1" from "DEN-1-1.inNetwork")
        const match = col.colId.match(/^([A-Z]+(-\d+)*-\d+)/);
        return match ? match[1] : null;
      })
      .filter((id): id is string => id !== null);

    // Remove duplicates (e.g., "DEN-1-1" appears multiple times for different networks)
    const uniquePlanOrder = Array.from(new Set(planColumns));

    return uniquePlanOrder.length > 0 ? uniquePlanOrder : null;
  } catch (error) {
    logger.error(
      "[ColumnOrderUtils] Error extracting plan order from event:",
      error as Error,
    );
    return null;
  }
};

/**
 * Extracts the carrier column group order from a column moved event.
 *
 * Iterates over all displayed columns in their visual order, inspects each
 * column's parent groups for a `groupId` matching `carrier-<carrierId>`, and
 * returns a deduplicated ordered list of carrier IDs.
 *
 * A carrier with more than one benefit plan group (e.g. dental-ppo) gets one
 * `carrier-<carrierId>` group for its first lane and `carrier-<carrierId>:<bpgId>`
 * groups for each additional lane — the `:<bpgId>` suffix must be stripped so
 * every lane resolves to the same real carrier ID, otherwise moving a
 * non-first lane records a bogus ID that the downstream reorder never
 * matches, leaving that carrier's columns behind.
 *
 * @param event - AgGrid ColumnMovedEvent (must have `finished === true`)
 * @returns Ordered array of carrier IDs, or null if not applicable
 */
export const extractCarrierOrderFromEvent = (
  event: ColumnMovedEvent,
): string[] | null => {
  try {
    if (!event.finished) return null;

    const allColumns = event.api.getAllDisplayedColumns();
    const carrierIds: string[] = [];
    const seen = new Set<string>();

    for (const col of allColumns) {
      // Walk up the column group hierarchy to find the carrier group
      let group = col.getParent();
      while (group) {
        const groupId = group.getGroupId();
        if (groupId?.startsWith("carrier-")) {
          const carrierId = groupId.replace("carrier-", "").split(":")[0];
          if (!seen.has(carrierId)) {
            seen.add(carrierId);
            carrierIds.push(carrierId);
          }
          break;
        }
        group = group.getParent();
      }
    }

    return carrierIds.length > 0 ? carrierIds : null;
  } catch (error) {
    logger.error(
      "[ColumnOrderUtils] Error extracting carrier order from event:",
      error as Error,
    );
    return null;
  }
};

/**
 * Extracts the carrier ID of the column(s) the user dragged, from the moved
 * event's own `column`/`columns` — as opposed to `extractCarrierOrderFromEvent`,
 * which derives the resulting order of every displayed carrier.
 *
 * A carrier's header spans several leaf columns (network sub-columns, plan
 * columns, …), so dragging it moves all of them together. AG Grid's own docs
 * for `ColumnMovedEvent` say `column` is "only set if action was on one
 * column" — for that ordinary group drag it's null, and the moved leaves are
 * in `columns` (plural) instead. Falling back to `columns[0]` is safe because
 * every leaf carried by a single drag belongs to the same carrier group.
 *
 * Knowing which single carrier was picked up (rather than inferring it by
 * diffing before/after order arrays) is what lets a reorder on one coverage
 * type propagate onto the cross-LOC carrier order without disturbing carriers
 * the drag didn't touch: an adjacent two-carrier swap is structurally
 * ambiguous about "which one moved" from the resulting order alone, but the
 * grid event itself isn't.
 *
 * @param event - AgGrid ColumnMovedEvent (must have `finished === true`)
 * @returns The dragged carrier's ID, or null if not applicable
 */
export const extractMovedCarrierIdFromEvent = (
  event: ColumnMovedEvent,
): string | null => {
  try {
    if (!event.finished) return null;

    const movedColumn = event.column ?? event.columns?.[0] ?? null;
    if (!movedColumn) return null;

    let group = movedColumn.getParent();
    while (group) {
      const groupId = group.getGroupId();
      if (groupId?.startsWith("carrier-")) {
        return groupId.replace("carrier-", "").split(":")[0];
      }
      group = group.getParent();
    }

    return null;
  } catch (error) {
    logger.error(
      "[ColumnOrderUtils] Error extracting moved carrier ID from event:",
      error as Error,
    );
    return null;
  }
};
